import pick from 'lodash/pick';
import {AttrsSchema, Component} from 'panel';
import DOMPurify from 'dompurify';

import {propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import css from './index.inline.styl';
import {template} from './template';

const GLOBAL_MIN_WIDTH = 0;
const SIZER_PROPS = [
  `fontFamily`,
  `fontSize`,
  `fontWeight`,
  `height`,
  `letterSpacing`,
  `padding-left`,
  `padding-right`,
  `whiteSpace`,
];

interface AutoResizingInputJsxProps {
  attrs: AutoResizingInputAttrs;
  on?: {
    input?: (ev) => void;
    focus?: (ev) => void;
    blur?: (ev) => void;
    keypress?: (ev) => void;
    change?: (ev) => void;
  };
}
interface AutoResizingInputState {
  inputValue: string;
  inputWidth: number;
}
interface AutoResizingInputAttrs {
  disabled?: boolean;
  focus?: boolean;
  'initial-width'?: number;
  'input-style-overrides': Obj<string>;
  'max-length'?: number;
  placeholder?: string;
  theme?: string;
}

export class AutoResizingInput extends Component<AutoResizingInputState, AutoResizingInputJsxProps['attrs']> {
  private _inputEl: HTMLInputElement;
  private sizer: HTMLDivElement;
  private minimumWidth: number;

  static get attrsSchema(): AttrsSchema<AutoResizingInputJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      disabled: `boolean`,
      focus: `boolean`,
      'initial-width': {type: `number`, default: GLOBAL_MIN_WIDTH},
      'input-style-overrides': `json`,
      'max-length': `number`,
      placeholder: `string`,
      theme: {type: `string`, enum: [`default`, `report-title`], default: `default`},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      updateSync: true,
      defaultState: {
        inputValue: ``,
        inputWidth: GLOBAL_MIN_WIDTH,
      },
      helpers: {
        inserted: (vnode) => {
          this._inputEl = vnode.elm;

          requestAnimationFrame(() => {
            if (this.attr(`focus`) && !this.attr(`disabled`)) {
              this.focus();
            } else {
              this.blur();
            }
          });
        },
        handleFocus: (ev: FocusEvent) => {
          this.setAttribute(`focus`, ``);
          propagateEvent(this, ev);
        },
        handleBlur: (ev: FocusEvent) => {
          this.removeAttribute(`focus`);
          propagateEvent(this, ev);
        },
        updatedInput: (ev: Event) => {
          this.value = (ev.target as HTMLInputElement).value;
          propagateEvent(this, ev);
        },
        propagateEvent: (ev: Event) => propagateEvent(this, ev),
      },
    };
  }

  constructor() {
    super();
    this.minimumWidth = GLOBAL_MIN_WIDTH;
  }

  connectedCallback() {
    super.connectedCallback();
    this.removeSizer();
    this.onDisconnected(() => this.removeSizer());
    this.setMinimumWidth(this.getTextWidth(this.attr(`placeholder`)));
    this.resize();
  }

  attributeChangedCallback(attr: string, oldVal: string, newVal: string) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (!this.isAttached && attr === `initial-width`) {
      this.setMinimumWidth(this.attr(attr));
    } else if (this.isAttached && attr === `placeholder`) {
      this.setMinimumWidth(this.getTextWidth(this.attr(attr)));
    }
  }

  removeSizer() {
    if (this.sizer) {
      this.sizer.remove();
      this.sizer = null;
    }
  }

  getTextWidth(text = ``) {
    if (!this.sizer) {
      this.sizer = this._initSizer();
    }

    // replace spaces with non-break spaces to prevent any measurement error caused by text wrapping
    this.sizer.innerHTML = DOMPurify.sanitize(text.replace(/ /g, `&nbsp;`));
    // add an extra 1px to prevent the input caret causing 1px horizontal scrolling glitch
    // add some extra space so content won't get cutoff
    return this.sizer.getBoundingClientRect().width + 5;
  }

  resize() {
    if (this.isAttached) {
      const newWidth = Math.max(this.minimumWidth, this.getTextWidth(this.state.inputValue));
      this.update({inputWidth: newWidth});
    }
  }

  setMinimumWidth(newMin) {
    if (newMin !== this.minimumWidth && newMin >= this.attr(`initial-width`)) {
      this.minimumWidth = newMin;
      this.resize();
    }
  }

  get isAttached() {
    return !!this.initialized;
  }

  get value() {
    return this.state.inputValue;
  }

  set value(inputValue) {
    this.update({inputValue});
    this.resize();
  }

  focus() {
    this._inputEl.focus();
  }

  blur() {
    this._inputEl.blur();
  }

  _initSizer() {
    const sizer = document.createElement(`div`);
    const input = this.el.querySelector(`input`);
    const inputStyle = window.getComputedStyle(input);
    Object.assign(sizer.style, pick(inputStyle, SIZER_PROPS));
    sizer.style.display = `inline-block`;
    sizer.style.position = `absolute`;
    sizer.style.left = `-9999px`;
    sizer.style.visibility = `hidden`;
    document.body.appendChild(sizer);
    return sizer;
  }
}

export const AutoResizingInputEl = defineAndMakeJsxFactory<AutoResizingInputJsxProps>(
  `mp-autoresizing-input`,
  AutoResizingInput,
);
export const AutoResizingInputReact = panel2react<AutoResizingInputJsxProps>(
  `mp-autoresizing-input`,
  AutoResizingInput,
);
